import api from "@/services/api";
import type {
  VendorAssessmentListFilterKeywords,
  VendorAssessmentListRequest,
  VendorAssessmentListResponse,
  VendorAssessmentListSortOrder,
} from "@/components/VendorAssessmentList/types/vendorAssessmentListApi.types";
import { EMPTY_VENDOR_ASSESSMENT_LIST_FILTER_KEYWORDS } from "@/components/VendorAssessmentList/types/vendorAssessmentListApi.types";
import { normalizeVendorAssessmentListResponse as normalizeListResponse } from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";

const { productServiceAPI } = api;

const VENDOR_ASSESSMENT_ACTIVE_LIST_PATH = "/idea/vendorAssessmentList/active";
const VENDOR_ASSESSMENT_ARCHIVED_LIST_PATH = "/idea/vendorAssessmentList/archived";

function normalizeStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

function normalizeVendorArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

function normalizeFilterKeywordsForPost(
  fk: VendorAssessmentListFilterKeywords | undefined
): VendorAssessmentListFilterKeywords {
  const o = fk ?? EMPTY_VENDOR_ASSESSMENT_LIST_FILTER_KEYWORDS;
  return {
    productType: normalizeStringArray(o.productType),
    businessStatus: normalizeStringArray(o.businessStatus),
    businessCategoryName: normalizeStringArray(o.businessCategoryName),
    categoryManager: normalizeStringArray(o.categoryManager),
    developer: normalizeStringArray(o.developer),
    vendor: normalizeVendorArray(o.vendor),
  };
}

function buildListPostBody(payload: VendorAssessmentListRequest): VendorAssessmentListRequest {
  const sortOrder: VendorAssessmentListSortOrder =
    payload.sortOrder === "ASC" ? "ASC" : "DESC";

  return {
    pageNo: Math.max(0, Math.floor(Number(payload.pageNo) || 0)),
    pageSize: Math.max(1, Math.floor(Number(payload.pageSize) || 10)),
    search: typeof payload.search === "string" ? payload.search : "",
    sortBy: typeof payload.sortBy === "string" && payload.sortBy.trim() ? payload.sortBy.trim() : "id",
    sortOrder,
    filterKeywords: normalizeFilterKeywordsForPost(payload.filterKeywords),
  };
}

async function postVendorAssessmentList(
  path: string,
  payload: VendorAssessmentListRequest
): Promise<VendorAssessmentListResponse> {
  const body = buildListPostBody(payload);
  const resp = await productServiceAPI.post<unknown>(path, body);

  const totalHeader =
    (resp as { headers?: Record<string, string> })?.headers?.["x-total-count"] ??
    (resp as { headers?: Record<string, string> })?.headers?.["X-Total-Count"];

  return normalizeListResponse(resp.data, body, totalHeader);
}

/** Re-export for list fetchers (`createListFetcher` normalize hook). */
export { normalizeVendorAssessmentListResponse } from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";

/**
 * POST `/idea/vendorAssessmentList/active` — paginated Active tab list.
 */
export async function postVendorAssessmentActiveList(
  payload: VendorAssessmentListRequest
): Promise<VendorAssessmentListResponse> {
  return postVendorAssessmentList(VENDOR_ASSESSMENT_ACTIVE_LIST_PATH, payload);
}

/**
 * POST `/idea/vendorAssessmentList/archived` — paginated Archived tab list.
 */
export async function postVendorAssessmentArchivedList(
  payload: VendorAssessmentListRequest
): Promise<VendorAssessmentListResponse> {
  return postVendorAssessmentList(VENDOR_ASSESSMENT_ARCHIVED_LIST_PATH, payload);
}

const VendorAssessmentListService = {
  postActiveList: postVendorAssessmentActiveList,
  postArchivedList: postVendorAssessmentArchivedList,
};

export default VendorAssessmentListService;
